/**
 * useChecklistConfig.js
 *
 * Generic hook for managing vessel-specific checklist configurations.
 * Works for any checklist type (Navigation, Operational, Emergency, etc.)
 *
 * FT-81: REFACTORED to use single storage key for cloud sync compatibility
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';

// Single storage key for ALL checklist configs (FT-81)
const STORAGE_KEY = 'checklist_configs';

// RBAC helper
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Get default config for a checklist
 */
const getDefaultConfig = (checklistId, baseItems = []) => ({
  version: 1,
  revision: 1,
  checklistId,
  items: baseItems.map(item => ({
    ...item,
    source: item.source || 'library',
    addedAt: new Date().toISOString(),
    addedBy: { name: 'System', userId: 'system' }
  })),
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing a specific checklist's configuration
 *
 * @param {string} checklistId - Unique ID for the checklist
 * @param {Array} baseItems - Default items from config (library items)
 * @returns {object} - Config and mutation functions
 */
export const useChecklistConfig = (checklistId, baseItems = []) => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();

  // FT-81: Use single storage key for ALL checklist configs
  // Data structure: { [checklistId]: { ...config }, ... }
  const { value: allConfigs, setValue: setAllConfigs } = useStorageState(
    STORAGE_KEY,
    {} // Empty object as default
  );

  // Get this checklist's config from the combined store
  const rawConfig = useMemo(() => {
    return allConfigs?.[checklistId] || null;
  }, [allConfigs, checklistId]);

  // FT-81: Helper to update just this checklist's config using functional updater
  // This avoids stale closure issues by reading from prev instead of allConfigs
  const setConfig = useCallback(async (newConfig) => {
    await setAllConfigs(prev => ({
      ...prev,
      [checklistId]: newConfig
    }));
  }, [checklistId, setAllConfigs]);

  // Merge base items with customizations
  const config = useMemo(() => {
    if (!rawConfig || !rawConfig.items) {
      return getDefaultConfig(checklistId, baseItems);
    }

    // Build merged items: start with base, overlay with stored customizations
    const mergedItems = [];

    // Add base items (may be overridden by stored)
    baseItems.forEach(baseItem => {
      const storedItem = rawConfig.items.find(i => i.id === baseItem.id);
      if (storedItem) {
        // Use stored version (might have edits)
        mergedItems.push(storedItem);
      } else {
        // Use base version
        mergedItems.push({
          ...baseItem,
          source: 'library',
          addedAt: new Date().toISOString(),
          addedBy: { name: 'System', userId: 'system' }
        });
      }
    });

    // Add custom items that aren't in base
    rawConfig.items
      .filter(item => item.source === 'custom' || !baseItems.find(b => b.id === item.id))
      .forEach(customItem => {
        if (!mergedItems.find(m => m.id === customItem.id)) {
          mergedItems.push(customItem);
        }
      });

    return {
      ...rawConfig,
      items: mergedItems
    };
  }, [rawConfig, baseItems, checklistId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const items = useMemo(() => config.items || [], [config]);

  const getCounts = useCallback(() => {
    const required = items.filter(i => i.required || i.regulation).length;
    const custom = items.filter(i => i.source === 'custom').length;
    const library = items.length - custom;
    return { total: items.length, required, custom, library };
  }, [items]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Add a new custom item
   */
  const addItem = useCallback(async (newItem) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useChecklistConfig] Permission denied');
      return null;
    }

    const itemWithMeta = {
      ...newItem,
      id: newItem.id || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: 'custom',
      addedAt: new Date().toISOString(),
      addedBy: { name: currentUser?.name || 'Unknown', userId: currentUser?.id || 'unknown' }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: [...config.items, itemWithMeta],
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    console.log('[useChecklistConfig] Added item:', itemWithMeta.id, 'to checklist:', checklistId);

    return itemWithMeta;
  }, [can, config, setConfig, currentUser, checklistId]);

  /**
   * Update an existing item
   * NOTE: All items can be edited EXCEPT those with locked: true
   * The 'required' flag only means "must complete during execution"
   */
  const updateItem = useCallback(async (itemId, updates) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useChecklistConfig] Permission denied');
      return false;
    }

    const existingItem = config.items.find(i => i.id === itemId);
    if (!existingItem) return false;

    // Only locked items cannot be edited
    if (existingItem.locked === true) {
      console.warn('[useChecklistConfig] Cannot edit locked items');
      return false;
    }

    const updatedItem = {
      ...existingItem,
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: config.items.map(i => i.id === itemId ? updatedItem : i),
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    console.log('[useChecklistConfig] Updated item:', itemId, 'in checklist:', checklistId);

    return true;
  }, [can, config, setConfig, currentUser, checklistId]);

  /**
   * Remove an item
   * NOTE: All items can be removed EXCEPT those with locked: true
   * The 'required' flag only means "must complete during execution"
   */
  const removeItem = useCallback(async (itemId, reason = '') => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useChecklistConfig] Permission denied');
      return false;
    }

    const existingItem = config.items.find(i => i.id === itemId);
    if (!existingItem) return false;

    // Only locked items cannot be removed
    if (existingItem.locked === true) {
      console.warn('[useChecklistConfig] Cannot remove locked items');
      return false;
    }

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: config.items.filter(i => i.id !== itemId),
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    console.log('[useChecklistConfig] Removed item:', itemId, 'from checklist:', checklistId, 'reason:', reason);

    return true;
  }, [can, config, setConfig, currentUser, checklistId]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Data
    config,
    items,
    checklistId,

    // Computed
    getCounts,

    // Mutations (with audit trail)
    addItem,
    updateItem,
    removeItem,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision
  };
};

export default useChecklistConfig;
